/**
 * SecurityRolesManagement - Manage security roles and permissions
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Shield, Plus, Edit2, Trash2, Check, X, Lock, 
  Users, Camera, Car, MapPin, FileText, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available permissions
const AVAILABLE_PERMISSIONS = [
  { id: 'patrol', label: 'Patrol Rounds', icon: MapPin, description: 'Can conduct patrol rounds' },
  { id: 'visitors', label: 'Visitor Management', icon: Users, description: 'Can register and manage visitors' },
  { id: 'vehicles', label: 'Vehicle Management', icon: Car, description: 'Can manage vehicle entry/exit' },
  { id: 'cameras', label: 'Camera Access', icon: Camera, description: 'Can view security cameras' },
  { id: 'reports', label: 'View Reports', icon: FileText, description: 'Can view security reports' },
  { id: 'manage_officers', label: 'Manage Officers', icon: Shield, description: 'Can manage other officers' },
];

// Color options for roles
const ROLE_COLORS = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#EF4444', label: 'Red' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#14B8A6', label: 'Teal' },
];

const SecurityRolesManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch roles
  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['security-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/security-roles`, { headers });
      if (!res.ok) throw new Error('Failed to fetch roles');
      return res.json();
    }
  });

  // Create role
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/security-roles`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create role');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['security-roles']);
      setShowAddDialog(false);
      toast.success('Security role created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update role
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/security-roles/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update role');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['security-roles']);
      setEditingRole(null);
      toast.success('Security role updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete role
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/security-roles/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete role');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['security-roles']);
      setDeleteConfirm(null);
      toast.success('Security role deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const RoleForm = ({ role, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      name: role?.name || '',
      description: role?.description || '',
      permissions: role?.permissions || [],
      color: role?.color || '#3B82F6'
    });

    const togglePermission = (permId) => {
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permId)
          ? prev.permissions.filter(p => p !== permId)
          : [...prev.permissions, permId]
      }));
    };

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Role Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Night Guard"
            data-testid="input-role-name"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Description</label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of this role"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Badge Color</label>
          <div className="flex gap-2 mt-2">
            {ROLE_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  formData.color === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: color.value }}
                onClick={() => setFormData({ ...formData, color: color.value })}
                title={color.label}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Permissions</label>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {AVAILABLE_PERMISSIONS.map((perm) => {
              const Icon = perm.icon;
              const isSelected = formData.permissions.includes(perm.id);
              return (
                <label
                  key={perm.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => togglePermission(perm.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-sm">{perm.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{perm.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-role-btn">
            {isLoading ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
          </Button>
        </div>
      </form>
    );
  };

  const systemRoles = roles.filter(r => r.is_system);
  const customRoles = roles.filter(r => !r.is_system);

  return (
    <div className="space-y-6" data-testid="security-roles-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Security Roles</h2>
          <p className="text-sm text-gray-500">Manage roles and permissions for security officers</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-role-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading roles...</div>
      ) : (
        <div className="space-y-6">
          {/* System Roles */}
          {systemRoles.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                System Roles (Read-only)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {systemRoles.map((role) => (
                  <Card key={role.id} className="bg-gray-50" data-testid={`role-card-${role.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: role.color }}
                          />
                          <span className="font-medium">{role.name}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">System</Badge>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">{role.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {(role.permissions || []).map((perm) => (
                          <Badge key={perm} variant="outline" className="text-xs">
                            {perm}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Custom Roles */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Custom Roles</h3>
            {customRoles.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Shield className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 mb-3">No custom roles yet</p>
                  <Button variant="outline" onClick={() => setShowAddDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Role
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customRoles.map((role) => (
                  <Card key={role.id} data-testid={`role-card-${role.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: role.color }}
                          />
                          <span className="font-medium">{role.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setEditingRole(role)}
                            data-testid={`edit-role-${role.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setDeleteConfirm(role)}
                            data-testid={`delete-role-${role.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">{role.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {(role.permissions || []).map((perm) => (
                          <Badge key={perm} variant="outline" className="text-xs">
                            {perm}
                          </Badge>
                        ))}
                        {(!role.permissions || role.permissions.length === 0) && (
                          <span className="text-xs text-gray-400">No permissions</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Role Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Security Role</DialogTitle>
          </DialogHeader>
          <RoleForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingRole} onOpenChange={(open) => !open && setEditingRole(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Security Role</DialogTitle>
          </DialogHeader>
          {editingRole && (
            <RoleForm
              role={editingRole}
              onSubmit={(data) => updateMutation.mutate({ id: editingRole.id, data })}
              onCancel={() => setEditingRole(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Role
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
              Officers assigned to this role will need to be reassigned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => deleteMutation.mutate(deleteConfirm.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityRolesManagement;
